import Scan from "./scan";

/**
 * Attendee.
 *
 * @param {Object}  params - Object parameter
 * @param {integer} params.id - Unique identifier
 * @param {string}  params.first_name - First Name
 * @param {string}  params.last_name - Last Name
 * @param {string}  params.email - Email
 * @param {integer} params.barcode - Barcode
 * @param {string}  params.paired_barcode - Paired Barcode
 * @param {string}  params.comment - Comment on attendee
 * @param {integer} params.rate - Id of rate
 * @param {string} params.rateName - Name of rate
 * @param {array}   params.scans - Array of scans
 * @param {string}  params.removed - DateTime of removal
 * @param {string}  params.created - DateTime of creation
 * @param {string}  params.modified - DateTime of Last Modification
 * @constructor
 */
export default class Attendee {
	constructor({
		id,
		first_name,
		last_name,
		email,
		barcode,
		paired_barcode,
		member_code,
		comment,
		rate,
		rate_name,
		scans,
		access_count,
		removed,
		created,
		modified
	} = {}) {
		this._id = id;
		this._first_name = first_name;
		this._last_name = last_name;
		this._email = email;
		this._barcode = barcode;
		this._paired_barcode = paired_barcode;
		this._member_code = member_code;
		this._comment = comment;
		this._rate = rate;
		this._rate_name = rate_name;
		this._scans = scans ? scans.map(scan => new Scan(scan)) : [];
		this._access_count = access_count;
		this._removed = removed;
		this._created = created;
		this._modified = modified;
	}

	id() {
		return this._id;
	}

	first_name() {
		return this._first_name ? this._first_name : "";
	}

	last_name() {
		return this._last_name ? this._last_name : null;
	}

	email() {
		return this._email;
	}

	barcode() {
		return this._barcode;
	}

	pairedBarcode() {
		return this._paired_barcode;
	}

	memberCode() {
		return this._member_code;
	}

	comment() {
		return this._comment;
	}

	rate() {
		return this._rate;
	}

	rateName() {
		return this._rate_name;
	}

	scans() {
		return this._scans;
	}

	accessCount() {
		return this._access_count;
	}

	getScans() {
		return this.scans().filter(
			scan => scan.status() !== 0 && scan.removed() === null
		);
	}

	hasScans() {
		return this.getScans().length > 0;
	}

	hasComment() {
		return this.comment() !== null && this.comment() !== "";
	}

	countScansIn() {
		return this.scans().filter(
			scan => scan.status() === 1 && scan.removed() === null
		).length;
	}

	countScansOut() {
		return this.scans().filter(
			scan => scan.status() === 2 && scan.removed() === null
		).length;
	}

	countScans() {
		return this.scans().filter(
			scan => scan.status() !== 0 && scan.removed() === null
		).length;
	}

	canScan() {
		return this.countScansIn() - this.countScansOut() < this._access_count;
	}

	getOnlyScanIn() {
		if (this.countScansIn() === 1) {
			return this.scans().filter(
				scan => scan.status() === 1 && scan.removed() === null
			)[0];
		}
		return null;
	}

	removed() {
		return this._removed;
	}

	created() {
		return this._created;
	}

	modified() {
		return this._modified;
	}

	setScans(scans = []) {
		this._scans = scans;
	}

	getFullName() {
		if (this._first_name && this._last_name) {
			return `${this._first_name} ${this._last_name}`;
		} else if (this._first_name) {
			return this._first_name;
		} else if (this._last_name) {
			return this._last_name;
		} else {
			return null;
		}
	}
}

Attendee.STATUS = {
	ACTIVE: "active",
	REMOVED: "removed"
};
